import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Bot,
  Calendar,
  Database,
  Store,
  CreditCard,
  WholeWord as WordPress,
  Zap,
  Server,
  Mail,
  Shield,
  MessageSquare,
  ArrowRight
} from 'lucide-react';
import SEO from '../../components/SEO';

const Solutions = () => {
  const { t } = useTranslation();

  const solutions = [
    { 
      icon: Bot, 
      title: t('solutions.sections.ai.title'),
      description: t('solutions.sections.ai.description'),
      path: '/lausnir/gervigreind'
    },
    { 
      icon: Calendar, 
      title: t('solutions.sections.booking.title'),
      description: t('solutions.sections.booking.description'),
      path: '/lausnir/bokunarkerfi'
    },
    { 
      icon: Database, 
      title: t('solutions.sections.subscription.title'),
      description: t('solutions.sections.subscription.description'),
      path: '/lausnir/askriftarkerfi'
    },
    { 
      icon: Store, 
      title: t('solutions.sections.ecommerce.title'),
      description: t('solutions.sections.ecommerce.description'),
      path: '/lausnir/netverslun'
    },
    { 
      icon: CreditCard, 
      title: t('solutions.sections.pos.title'),
      description: t('solutions.sections.pos.description'),
      path: '/lausnir/kassakerfi'
    },
    { 
      icon: WordPress, 
      title: t('solutions.sections.wordpress.title'),
      description: t('solutions.sections.wordpress.description'),
      path: '/lausnir/wordpress'
    },
    { 
      icon: Zap, 
      title: t('solutions.sections.wordpressExpress.title'),
      description: t('solutions.sections.wordpressExpress.description'),
      path: '/lausnir/wordpress-express'
    },
    { 
      icon: Server, 
      title: t('solutions.sections.hosting.title'),
      description: t('solutions.sections.hosting.description'),
      path: '/lausnir/vefhysing'
    },
    { 
      icon: Mail, 
      title: t('solutions.sections.email.title'),
      description: t('solutions.sections.email.description'),
      path: '/lausnir/tolvupostur'
    },
    { 
      icon: Shield, 
      title: t('solutions.sections.management.title'),
      description: t('solutions.sections.management.description'),
      path: '/lausnir/vefumsjon'
    },
    {
      icon: MessageSquare,
      title: t('solutions.sections.chat.title'),
      description: t('solutions.sections.chat.description'),
      path: '/lausnir/netspjall'
    }
  ];

  return (
    <>
      <SEO 
        title={t('common.solutions')}
        description={t('solutions.description')}
        keywords={t('site.keywords')}
      />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <motion.div 
              className="text-center max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                {t('solutions.title')}
              </h1>
              <p className="text-xl text-gray-600">
                {t('solutions.description')}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Solutions Grid */}
        <div className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {solutions.map((solution, index) => (
                <motion.div
                  key={index}
                  className="bg-gray-50 rounded-xl p-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <Link to={solution.path} className="block">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                      <solution.icon className="w-6 h-6 text-[#EA295A]" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{solution.title}</h3>
                    <p className="text-gray-600 mb-6">{solution.description}</p>
                    <span className="inline-flex items-center text-[#EA295A] font-medium">
                      {t('common.readMore')}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
              <motion.h2 
                className="text-3xl font-bold text-white mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                {t('solutions.cta.title')}
              </motion.h2>
              <motion.p 
                className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                {t('solutions.cta.description')}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                  >
                    {t('common.getQuote')}
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Solutions;